import type { ReactNode } from "react";

import { CloseIcon } from "../..";
import { Button } from "../button";
import { BodySkeleton, HeaderSkeleton } from "./side-panel-skeleton";

import styles from "./side-panel.module.css";

type ContentProps = {
	content: ReactNode;
	isLoading: boolean;
};

type HeaderContentProps = { onClose: () => void } & ContentProps;

function HeaderContent({ content, isLoading, onClose }: HeaderContentProps): ReactNode {
	const title = typeof content === "string" ? <h2 className="title-2">{content}</h2> : content;

	return (
		<header className={styles.header}>
			{isLoading ? <HeaderSkeleton /> : title}
			<Button
				data-testid="side-panel-close-button"
				iconOnly
				onPress={onClose}
				variant="secondary"
			>
				<CloseIcon />
			</Button>
		</header>
	);
}

function BodyContent({ content, isLoading }: ContentProps): ReactNode {
	return <main className={styles.body}>{isLoading ? <BodySkeleton /> : content}</main>;
}

type WithFooter = { footer?: ReactNode; showFooter?: true } | { showFooter: false };
type FooterContentProps = WithFooter & { isLoading: boolean };

function FooterContent({ isLoading, ...props }: FooterContentProps): ReactNode {
	if (isLoading || !props.showFooter) {
		return null;
	}

	return <footer className={styles.footer}>{props.footer}</footer>;
}

type SidePanelProps = {
	body: ReactNode;
	header: ReactNode;
	isLoading: boolean;
	onClose: () => void;
} & WithFooter;

export function SidePanel({ body, header, isLoading, onClose, ...props }: SidePanelProps) {
	return (
		<aside className={styles.sidePanelContainer} data-testid="side-panel">
			<HeaderContent content={header} isLoading={isLoading} onClose={onClose} />
			<BodyContent content={body} isLoading={isLoading} />
			<FooterContent isLoading={isLoading} {...props} />
		</aside>
	);
}
